import { useRef, useState } from 'react'

import { useLocalization } from '@/shared/i18n'
import { normalizePhoto } from '@/shared/image'
import { Button, Icon, Stack, Text } from '@/shared/ui'
import { putFile, useLocalPreview } from '@/shared/vault'

import type { AnswerProps } from './types'

const PHOTOS = 'image/jpeg,image/png'

export function DocumentAnswer({ question, value, onChange }: AnswerProps) {
  const { t } = useLocalization()
  const picker = useRef<HTMLInputElement>(null)
  const [saving, setSaving] = useState(false)

  const path = typeof value === 'string' ? value : undefined
  const preview = useLocalPreview(path)

  function choose() {
    picker.current?.click()
  }

  async function take(picked: File | undefined) {
    if (!picked || !question.folder) {
      return
    }

    setSaving(true)

    const name = `${Date.now()}-${question.tag}.jpg`
    const photo = await normalizePhoto(picked, name)
    const kept = `${question.folder}/${name}`

    await putFile(kept, photo)

    setSaving(false)
    onChange(kept)
  }

  return (
    <Stack gap="sm" className="ask__control">
      {preview ? (
        <img className="ask__photo" src={preview} alt={question.text} />
      ) : (
        <button className="ask__capture" type="button" disabled={saving} onClick={choose}>
          <Icon size="lg">📷</Icon>
          <Text role="label">
            {saving ? t('survey.document.saving') : t('survey.document.add')}
          </Text>
        </button>
      )}

      {preview ? (
        <Button variant="quiet" full disabled={saving} onClick={choose}>
          {t('survey.document.retake')}
        </Button>
      ) : null}

      <input
        ref={picker}
        className="visually-hidden"
        type="file"
        accept={PHOTOS}
        tabIndex={-1}
        aria-hidden
        onChange={(event) => {
          const picked = event.target.files?.[0]
          event.target.value = ''
          void take(picked)
        }}
      />
    </Stack>
  )
}
